/**
 * Extend Variant Picker Handler (Optimized)
 * 
 * Performance Improvements:
 * ✅ Adds caching for price snippet fetches
 * ✅ Debounces variant focus/price updates
 * ✅ Minimizes DOM parsing & redundant network calls
 */
document.addEventListener('DOMContentLoaded', () => {
  initializeExtendPickers();
});

function initializeExtendPickers() {
  const extendVariantSelects = document.querySelectorAll('variant-selects[data-extend]');
  extendVariantSelects.forEach(variantSelect => new ExtendVariantPickerHandler(variantSelect));
}

class ExtendVariantPickerHandler {
  static priceCache = new Map();

  constructor(element) {
    this.variantSelect = element;
    this.sectionId = element.dataset.section;
    this.productInfo = element.closest('product-info');
    this.productForm = this.productInfo?.querySelector('product-form form');
    this.variantIdInput = this.productForm?.querySelector('input[name="id"]');

    this.quantityInput =
      this.variantSelect.querySelector('input[name="quantity"][data-extend-quantity-hidden]') ||
      this.productForm?.querySelector('input[name="quantity"]');

    this.variantItems = this.variantSelect.querySelectorAll('.variant-extend-item');
    this.quantityInputs = this.variantSelect.querySelectorAll('.variant-extend-quantity-input');
    this.radioInputs = this.variantSelect.querySelectorAll('.variant-extend-radio');

    // Debounced price update
    this.debouncedUpdatePrice = this.debounce(this.updatePriceSnippet.bind(this), 150);

    this.init();
  }

  // ---- Utility ----
  debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), delay);
    };
  }

  // ---- Initialization ----
  init() {
    this.variantSelect.addEventListener(
      'change',
      event => {
        if (
          event.target.classList.contains('variant-extend-radio') ||
          event.target.classList.contains('variant-extend-quantity-input')
        ) {
          event.stopImmediatePropagation();
        }
      },
      true
    );

    this.quantityInputs.forEach(input => {
      input.addEventListener('change', this.handleQuantityChange.bind(this));
      input.addEventListener('input', this.handleQuantityChange.bind(this));
      input.addEventListener('focus', this.handleQuantityFocus.bind(this));
    });

    const quantityButtons = this.variantSelect.querySelectorAll('[data-extend-quantity] .quantity__button');
    quantityButtons.forEach(button => {
      button.addEventListener('click', this.handleQuantityButtonClick.bind(this));
    });

    this.radioInputs.forEach(radio => {
      radio.addEventListener('change', this.handleRadioChange.bind(this));
      radio.addEventListener('focus', this.handleRadioFocus.bind(this));
    });

    if (this.productForm) {
      this.productForm.addEventListener('submit', this.handleFormSubmit.bind(this), true);
    }
  }

  // ---- Quantity & Radio Handlers ----
  handleQuantityFocus(event) {
    const input = event.target;
    const variantId = input.dataset.variantId;
    const radio = this.variantSelect.querySelector(
      `input.variant-extend-radio[data-variant-id="${variantId}"]`
    );

    if (radio && !radio.checked) {
      radio.checked = true;
      const quantity = parseInt(input.value) || 1;
      this.updateSelectedVariant(variantId, quantity);
    }
  }

  handleQuantityButtonClick(event) {
    const button = event.target.closest('.quantity__button');
    if (!button) return;

    const quantityInput = button
      .closest('[data-extend-quantity]')
      .querySelector('.variant-extend-quantity-input');
    const variantId = quantityInput.dataset.variantId;
    const radio = this.variantSelect.querySelector(
      `input.variant-extend-radio[data-variant-id="${variantId}"]`
    );

    if (radio && !radio.checked) {
      radio.checked = true;
      setTimeout(() => {
        const quantity = parseInt(quantityInput.value) || 1;
        this.updateSelectedVariant(variantId, quantity);
      }, 0);
    }
  }

  handleQuantityChange(event) {
    const input = event.target;
    const variantId = input.dataset.variantId;
    const quantity = parseInt(input.value) || 1;
    const radio = this.variantSelect.querySelector(
      `input.variant-extend-radio[data-variant-id="${variantId}"]`
    );

    if (radio && radio.checked) {
      this.updateSelectedVariant(variantId, quantity);
    }
  }

  handleRadioFocus(event) {
    const radio = event.target;
    const variantId = radio?.dataset?.variantId;
    if (!variantId) return;
    if (!radio.checked) radio.checked = true;

    this.updateSelectedVariantIdOnly(variantId);
    // Debounced price update
    this.debouncedUpdatePrice(variantId);
  }

  handleRadioChange(event) {
    const radio = event.target;
    const variantId = radio.dataset.variantId;
    this.updateSelectedVariantIdOnly(variantId);
  }

  // ---- Variant Management ----
  updateSelectedVariant(variantId, quantity) {
    if (this.variantIdInput) {
      this.variantIdInput.value = variantId;
      this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.quantityInput) {
      this.quantityInput.value = quantity;
    }

    const variantsData = JSON.parse(
      this.variantSelect.querySelector('[data-variants]')?.textContent || '[]'
    );
    const selectedVariant = variantsData.find(v => v.id == variantId);

    if (selectedVariant) {
      const selectedVariantElement = this.variantSelect.querySelector('[data-selected-variant]');
      if (selectedVariantElement) {
        selectedVariantElement.textContent = JSON.stringify(selectedVariant);
      }

      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.reloadProductInfoWithVariant(variantId);

        const checkedRadio = this.variantSelect.querySelector('.variant-extend-radio:checked');
        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event: new Event('change'),
            target: checkedRadio || this.variantSelect,
            selectedOptionValues: [],
          },
        });
      }
    }
  }

  updateSelectedVariantIdOnly(variantId) {
    const variantQuantityInput = this.variantSelect.querySelector(
      `input.variant-extend-quantity-input[data-variant-id="${variantId}"]`
    );
    const quantity =
      parseInt(variantQuantityInput?.value) ||
      parseInt(this.quantityInput?.value) ||
      1;
    this.updateSelectedVariant(variantId, quantity);
  }

  reloadProductInfoWithVariant(variantId) {
    const sectionId = this.productInfo?.dataset.section || this.sectionId;
    const productUrl = this.productInfo?.dataset.url;
    const url = `${productUrl}?variant=${variantId}&section_id=${sectionId}`;

    fetch(url)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const customLiquidBlocks = this.productInfo.querySelectorAll('.custom__liquid');

        customLiquidBlocks.forEach(block => {
          if (block.id) {
            const updatedBlock = html.querySelector(`#${block.id}`);
            if (updatedBlock) {
              const parentDetails = block.closest('details');
              const wasOpen = parentDetails?.open || false;

              block.innerHTML = updatedBlock.innerHTML;
              if (parentDetails && wasOpen) parentDetails.open = true;

              block.dispatchEvent(
                new CustomEvent('variant-content-updated', {
                  bubbles: true,
                  detail: { variantId },
                })
              );
            }
          }
        });
      })
      .catch(error => console.error('Error reloading product info:', error));
  }

  // ---- Optimized Price Update ----
  async updatePriceSnippet(variantId) {
    try {
      const sectionId =
        this.productInfo?.dataset.originalSection ||
        this.productInfo?.dataset.section ||
        this.sectionId;
      const productUrl = this.productInfo?.dataset.url;
      if (!sectionId || !productUrl) return;

      const cacheKey = `${productUrl}?variant=${variantId}&section_id=${sectionId}`;
      let htmlDoc;

      // ✅ Cache lookup
      if (ExtendVariantPickerHandler.priceCache.has(cacheKey)) {
        htmlDoc = ExtendVariantPickerHandler.priceCache.get(cacheKey);
      } else {
        const response = await fetch(cacheKey);
        const responseText = await response.text();
        htmlDoc = new DOMParser().parseFromString(responseText, 'text/html');
        ExtendVariantPickerHandler.priceCache.set(cacheKey, htmlDoc);
      }

      // ✅ Replace only necessary elements
      const targets = [`price-${sectionId}`, `Price-Alt-${sectionId}`];
      targets.forEach(id => {
        const source = htmlDoc.getElementById(id);
        const destination = this.productInfo?.querySelector(`#${id}`);
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
          destination.classList.toggle('hidden', source.classList.contains('hidden'));
        }
      });
    } catch (e) {
      console.error('Error in updatePriceSnippet:', e);
    }
  }

  // ---- Form Submit ----
  handleFormSubmit(event) {
    const selectedRadio = this.variantSelect.querySelector('.variant-extend-radio:checked');
    if (!selectedRadio) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }

    const selectedVariantId = selectedRadio.dataset.variantId;
    const quantityInput = this.variantSelect.querySelector(
      `input.variant-extend-quantity-input[data-variant-id="${selectedVariantId}"]`
    );
    const quantity = parseInt(quantityInput?.value) || 1;

    if (quantity <= 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }

    if (this.variantIdInput) this.variantIdInput.value = selectedVariantId;
    if (this.quantityInput) this.quantityInput.value = quantity;

    return true;
  }
}

if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    initializeExtendPickers();
  });
}
